import { useEffect } from "react";

/**
 * Small per-page SEO hook — sets <title>, description, canonical, OG tags.
 * Restores previous values on unmount so we don't "leak" page-specific copy
 * into pages that haven't set their own.
 */
function upsertMeta(selector, attr, value) {
  if (!value) return null;
  let tag = document.head.querySelector(selector);
  const created = !tag;
  if (created) {
    tag = document.createElement("meta");
    const [name, key] = selector.slice(5, -1).split("=");
    tag.setAttribute(name, key.replace(/"/g, ""));
    document.head.appendChild(tag);
  }
  const prev = tag.getAttribute(attr);
  tag.setAttribute(attr, value);
  return { tag, prev, created };
}

export default function useSeo({ title, description, canonical, image, keywords } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const cleanupOps = [];

    if (description) {
      const r = upsertMeta('meta[name="description"]', "content", description);
      if (r) cleanupOps.push(() => { if (r.created) r.tag.remove(); else r.tag.setAttribute("content", r.prev || ""); });
      const og = upsertMeta('meta[property="og:description"]', "content", description);
      if (og) cleanupOps.push(() => { if (og.created) og.tag.remove(); else og.tag.setAttribute("content", og.prev || ""); });
      const tw = upsertMeta('meta[name="twitter:description"]', "content", description);
      if (tw) cleanupOps.push(() => { if (tw.created) tw.tag.remove(); else tw.tag.setAttribute("content", tw.prev || ""); });
    }

    if (title) {
      const og = upsertMeta('meta[property="og:title"]', "content", title);
      if (og) cleanupOps.push(() => { if (og.created) og.tag.remove(); else og.tag.setAttribute("content", og.prev || ""); });
      const tw = upsertMeta('meta[name="twitter:title"]', "content", title);
      if (tw) cleanupOps.push(() => { if (tw.created) tw.tag.remove(); else tw.tag.setAttribute("content", tw.prev || ""); });
    }

    if (keywords) {
      const r = upsertMeta('meta[name="keywords"]', "content", keywords);
      if (r) cleanupOps.push(() => { if (r.created) r.tag.remove(); else r.tag.setAttribute("content", r.prev || ""); });
    }

    if (image) {
      const og = upsertMeta('meta[property="og:image"]', "content", image);
      if (og) cleanupOps.push(() => { if (og.created) og.tag.remove(); else og.tag.setAttribute("content", og.prev || ""); });
      const tw = upsertMeta('meta[name="twitter:image"]', "content", image);
      if (tw) cleanupOps.push(() => { if (tw.created) tw.tag.remove(); else tw.tag.setAttribute("content", tw.prev || ""); });
    }

    let canonicalLink;
    let prevCanonicalHref = null;
    let createdCanonical = false;
    if (canonical) {
      canonicalLink = document.head.querySelector('link[rel="canonical"]');
      if (!canonicalLink) {
        canonicalLink = document.createElement("link");
        canonicalLink.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalLink);
        createdCanonical = true;
      } else {
        prevCanonicalHref = canonicalLink.getAttribute("href");
      }
      canonicalLink.setAttribute("href", canonical);
    }

    return () => {
      if (title) document.title = prevTitle;
      cleanupOps.forEach((op) => op());
      if (canonicalLink) {
        if (createdCanonical) canonicalLink.remove();
        else if (prevCanonicalHref) canonicalLink.setAttribute("href", prevCanonicalHref);
      }
    };
  }, [title, description, canonical, image, keywords]);
}
